import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow } from "@/components/Reveal";

const OWNER_IMG = "https://images.unsplash.com/photo-1587241321921-91a834d6d191?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBsb2NhbCUyMHNob3AlMjBjYWZlJTIwYmFrZXJ5JTIwY3JhZnRzbWFufGVufDB8fHx8MTc5MTY3OTAzOXww&ixlib=rb-4.1.0&q=85";

const values = [
  { title: "Honest pricing", copy: "You get a clear quote before we start, and that's what you pay. No surprise line items, ever." },
  { title: "Plain language", copy: "We explain things the way we'd explain them to a neighbour — no jargon, no condescension." },
  { title: "Local first", copy: "Money spent here stays here. We answer our own phone and we shop at the same stores you do." },
  { title: "Built to last", copy: "Websites built like good tools: sturdy, quick, and easy to maintain for years to come." },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About | Good Work Web Co."
      description="Good Work Web Co. is a small, local web studio in the Almaguin Highlands building honest, affordable websites for the people who keep Northern Ontario running."
    />

    <section className="py-12 sm:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow>About Good Work Web Co.</Eyebrow>
            <h1 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#233D35] mt-3 leading-[0.95]" data-testid="about-heading">
              A neighbour who builds websites.
            </h1>
            <div className="mt-6 space-y-4 text-[#77786F] text-base sm:text-lg leading-relaxed">
              <p>
                Good Work Web Co. started with a simple observation: the people doing the hardest, most honest work in our communities — the carpenters, the bakers, the outfitters, the folks who plow the roads and fix the furnaces — often have the hardest time getting found online.
              </p>
              <p>
                Big agencies charge big-city prices and talk in acronyms. DIY builders eat your weekends and still look half-finished. We figured there should be something in between: a local shop that builds proper, professional websites at prices a working business can actually afford.
              </p>
              <p>
                So that's what we do. Based in the Almaguin Highlands, we build websites the way a good carpenter builds a deck — measured twice, built once, and made to hold up through every season.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="about-quote-cta"
              className="group mt-8 inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-7 py-4 transition-colors duration-200"
            >
              Get a Free Quote
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#C58A47]" aria-hidden="true" />
              <img
                src={OWNER_IMG}
                alt="A small business owner at work in their local shop"
                className="relative w-full h-[340px] sm:h-[480px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-[#EBE5D8] py-12 sm:py-16" data-testid="values-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>How We Work</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-3 leading-[0.95]">
            Four things we won't compromise on.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="border-t-2 border-[#233D35] pt-5" data-testid={`value-${i}`}>
                <span className="font-head font-bold text-[#C58A47] text-lg">0{i + 1}</span>
                <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#314F64] mt-2">{v.title}</h3>
                <p className="mt-2 text-sm text-[#77786F] leading-relaxed">{v.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="grain bg-[#1A2621] text-[#F3EFE5]" data-testid="local-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-[2]">
        <Reveal className="max-w-3xl">
          <Eyebrow light>Local Roots</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[0.95]">
            From Burk's Falls to South River, we've got you covered.
          </h2>
          <p className="mt-5 text-[#F3EFE5]/75 text-base sm:text-lg leading-relaxed">
            We serve the whole Almaguin Highlands — Burk's Falls, Sundridge, South River, Magnetawan, Kearney, Sprucedale, and the back roads in between. If your business keeps this region going, we want to help people find it.
          </p>
        </Reveal>
      </div>
    </section>

    <CtaBand title="Sound like your kind of people?" />
  </div>
);

export default About;
